import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaExclamationTriangle } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "warning") return "#f59e0b";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "warning") return "#f59e0b";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const TemplateBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background-color: ${({ theme }) =>
    theme.isdarkmode ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.04)"};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 0.75rem;
  }

  td {
    padding: 0.5rem 0.5rem 0.5rem 0;
    border-bottom: 1px solid ${({ theme }) => theme.iconColor}40;
    vertical-align: top;
  }

  td:first-child {
    font-weight: 600;
    width: 40%;
  }
`;

const ScenarioBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    color: ${({ theme }) => theme.iconColor};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  ul {
    margin: 0.5rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const VendorRiskGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.VENDOR_RISK);

  // const heroImageSrc = "/images/guides/vendor-risk.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Manage Third-Party & Vendor Access Safely"
        seoTitle="Vendor Risk Guide: Third-Party Access Management | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Practical guide to vetting vendors, controlling third-party access, monitoring ongoing risk, and offboarding vendors safely for SMBs."
      ogTitle="Vendor Risk Guide: Third-Party Access Management | CSFSafe"
      ogDescription="Vendor assessment checklist, access approval template, and incident response SLA guidance for managing third-party risk."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Vendor and third-party risk management"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        Every vendor with access to your systems, data, or network is an
        extension of your attack surface. A weak vendor is often the easiest
        path into an otherwise well-protected organization. This guide covers
        vetting, access control, ongoing monitoring, and safe offboarding for
        third parties.
      </Paragraph>

      <CalloutBox $type="warning">
        <strong>Why Vendor Risk Matters</strong>
        <ul>
          <li>
            Attackers increasingly target smaller, less-protected vendors to
            reach their larger customers ("supply chain" attacks)
          </li>
          <li>
            A single vendor with broad, unmonitored access can expose your
            entire network if compromised
          </li>
          <li>
            Many data breach notification laws hold you responsible even when
            a vendor caused the breach
          </li>
        </ul>
      </CalloutBox>

      <H2>Vendor Vetting</H2>

      <Paragraph>
        Before granting any vendor access to your systems or data, understand
        what you're trusting them with and how seriously they take security.
      </Paragraph>

      <H3>Before You Grant Access</H3>

      <ChecklistSection>
        <h3>Vendor Assessment Checklist</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            What data or systems will this vendor access, and is that access
            truly necessary?
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            Does the vendor have a security certification (SOC 2, ISO 27001)
            or a completed security questionnaire?
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            Does their contract include data protection, confidentiality, and
            breach notification clauses?
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            Do they carry cyber liability insurance, and can they provide
            proof?
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            What subcontractors or fourth parties do they use, and do those
            also touch your data?
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            Have they had a publicly known breach or security incident, and
            how did they respond?
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">
            Do they support MFA, encryption, and role-based access for any
            accounts they'll create for you or that you'll create for them?
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Access Control Requirements</H2>

      <H3>Least Privilege for Third Parties</H3>

      <CalloutBox $type="do">
        <strong>Apply Least Privilege to Every Vendor</strong>
        <ul>
          <li>
            Grant access only to the specific systems and data needed for the
            vendor's function—nothing broader
          </li>
          <li>
            Use time-limited or scoped credentials rather than permanent
            standing access when possible
          </li>
          <li>
            Require MFA for any vendor account with access to your systems
          </li>
          <li>
            Use a dedicated vendor account (never a shared generic login) so
            activity is traceable to that specific vendor
          </li>
          <li>
            Log and monitor vendor account activity the same way you would an
            employee account
          </li>
          <li>
            Segment vendor access from your core network where technically
            possible (separate VLAN, VPN profile, or jump box)
          </li>
        </ul>
      </CalloutBox>

      <H2>Access Approval Template</H2>

      <TemplateBox>
        <h3>Vendor Access Request Form (Starting Point)</h3>
        <table>
          <tbody>
            <tr>
              <td>Vendor Name</td>
              <td>[Company name]</td>
            </tr>
            <tr>
              <td>Business Justification</td>
              <td>[Why access is needed]</td>
            </tr>
            <tr>
              <td>Systems/Data Requested</td>
              <td>[Specific systems, scope of data]</td>
            </tr>
            <tr>
              <td>Access Level</td>
              <td>[Read-only / Read-write / Admin]</td>
            </tr>
            <tr>
              <td>Duration</td>
              <td>[Start date – End date, or "ongoing, reviewed quarterly"]</td>
            </tr>
            <tr>
              <td>MFA Required?</td>
              <td>[Yes/No — should always be Yes]</td>
            </tr>
            <tr>
              <td>Approved By</td>
              <td>[Name, Title, Date]</td>
            </tr>
            <tr>
              <td>Review Date</td>
              <td>[Next scheduled access review]</td>
            </tr>
          </tbody>
        </table>
      </TemplateBox>

      <H2>Ongoing Monitoring</H2>

      <CalloutBox $type="tip">
        <strong>Don't Vet Once and Forget</strong>
        <ul>
          <li>
            Review vendor access quarterly—confirm it's still needed and still
            scoped correctly
          </li>
          <li>
            Monitor vendor account activity for unusual times, locations, or
            data volumes
          </li>
          <li>
            Re-assess vendor security posture annually, or after any known
            security incident on their end
          </li>
          <li>
            Track vendor certifications and insurance for expiration
          </li>
          <li>
            Maintain an up-to-date inventory of all vendors with system or
            data access—you can't monitor what you don't know about
          </li>
        </ul>
      </CalloutBox>

      <H2>Incident Response SLA Template</H2>

      <TemplateBox>
        <h3>Vendor Incident Notification Requirements (Include in Contracts)</h3>
        <table>
          <tbody>
            <tr>
              <td>Notification Window</td>
              <td>Vendor must notify us within [24-72 hours] of discovering
                an incident affecting our data or systems</td>
            </tr>
            <tr>
              <td>Notification Method</td>
              <td>[Designated contact/email/phone] with written follow-up</td>
            </tr>
            <tr>
              <td>Information Required</td>
              <td>Nature of incident, data/systems affected, containment
                actions taken, timeline</td>
            </tr>
            <tr>
              <td>Cooperation Clause</td>
              <td>Vendor agrees to cooperate with our investigation and provide
                requested logs/evidence</td>
            </tr>
            <tr>
              <td>Remediation Responsibility</td>
              <td>[Defines who bears cost of notification, credit monitoring,
                remediation if vendor is at fault]</td>
            </tr>
          </tbody>
        </table>
      </TemplateBox>

      <H2>Offboarding Procedures</H2>

      <ChecklistSection>
        <h3>When a Vendor Relationship Ends</h3>
        <ChecklistItem>
          <input type="checkbox" id="item8" />
          <label htmlFor="item8">
            Disable all vendor accounts and access on the effective end date
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item9" />
          <label htmlFor="item9">
            Revoke API keys, VPN credentials, and shared logins immediately
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item10" />
          <label htmlFor="item10">
            Confirm the vendor has returned or securely destroyed your data,
            with written confirmation
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item11" />
          <label htmlFor="item11">
            Remove the vendor from your active vendor inventory and access
            review list
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item12" />
          <label htmlFor="item12">
            Review any remaining integrations, webhooks, or automated data
            feeds and disable them
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Red Flags for High-Risk Vendors</H2>

      <ScenarioBox>
        <h4>
          <FaExclamationTriangle /> Watch For These Warning Signs
        </h4>
        <ul>
          <li>Vendor refuses to complete a basic security questionnaire</li>
          <li>No formal incident response process or unclear breach history</li>
          <li>Requests broader access than their function requires</li>
          <li>No MFA support on their platform or for the accounts they issue</li>
          <li>Uses shared, generic logins instead of individual accounts</li>
          <li>Has had a recent, unresolved public security incident</li>
          <li>Subcontracts your data to other vendors without disclosure</li>
          <li>Pushes back on including security/notification clauses in contracts</li>
        </ul>
      </ScenarioBox>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.ARTICLE.THIRD_PARTY_RISK_MANAGEMENT)}>
            Third Party Risk Management
          </a>
        </li>
        <li>
          <a href={href(LINKS.BEST_PRACTICES.VENDOR_RISK_MANAGEMENT)}>
            Vendor Risk Management: Full Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.CODERED)}>
            CodeRed: Vendor Risk Case Study
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.SUPPLY_CHAIN)}>
            Supply Chain Threat Type
          </a>
        </li>
        <li>
          <a href={href(LINKS.BEST_PRACTICES.ACCESS_CONTROL)}>
            Access Control &amp; Password Basics
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/vendor-risk"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

VendorRiskGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default VendorRiskGuide;
